/**
 * Theme Context
 * Manages both dark/light mode AND color themes with localStorage persistence
 * Default: dark mode + terracotta colors
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { themes, Theme as ColorTheme, defaultTheme } from '../theme/themes';

type DarkLightMode = 'dark' | 'light';

interface ThemeContextType {
  // Dark/Light mode
  theme: DarkLightMode;
  toggleTheme: () => void;
  isDark: boolean;

  // Color theme
  colorTheme: ColorTheme;
  setColorTheme: (themeId: string) => void;
  availableColorThemes: ColorTheme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Dark/Light mode state
  const [theme, setTheme] = useState<DarkLightMode>(() => {
    const stored = localStorage.getItem('classrooms-theme-v2');
    return (stored === 'light' || stored === 'dark') ? stored : 'light';
  });

  // Color theme state
  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => {
    const savedThemeId = localStorage.getItem('classrooms-color-theme-v6');
    if (savedThemeId) {
      const savedTheme = themes.find(t => t.id === savedThemeId);
      if (savedTheme) {
        return savedTheme;
      }
    }
    return defaultTheme;
  });

  // Apply dark/light mode
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('classrooms-theme-v2', theme);
  }, [theme]);

  // Apply color theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', colorTheme.id);
  }, [colorTheme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const setColorTheme = (themeId: string) => {
    const newTheme = themes.find(t => t.id === themeId);
    if (newTheme) {
      setColorThemeState(newTheme);
      localStorage.setItem('classrooms-color-theme-v6', themeId);
    }
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      toggleTheme,
      isDark: theme === 'dark',
      colorTheme,
      setColorTheme,
      availableColorThemes: themes
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
